import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { FormattedMessage, useIntl } from "react-intl";
import { Input, TextArea } from "sr/helpers";
import { Button } from "sr/helpers/ui-components/company/Button";
import { LevelBar } from "sr/helpers/ui-components/LevelBar";
import { MainWrapper } from "../../component/MainWrapper";
import ContentWithSpinner from "sr/layout/common/ContentWithSpiner";
import { LoaderFullScreen } from "sr/layout/common/LoaderFullScreen";
import { getUserDetails } from "sr/helpers/functions/getUserDetails";
import { useValidationErrorHandle } from "sr/helpers/functions/validationErrorHandle";
import { scrollToError } from "sr/helpers/functions/scrollToError";
import DropDown from "sr/helpers/ui-components/DropDown.tsx";
import { toast } from "react-toastify";

import { createBidding } from "sr/utils/api/corporate/biddings";
import { getRequirementDetails } from "sr/utils/api/corporate/requirements";
import { uploadFile } from "sr/utils/api/uploadFile";
import { RequirementCommonHeader } from "app/pages/corporate/layout/RequirementCommonHeader";
import DocumentUpload from "sr/helpers/ui-components/DocumentUpload";
import { BidSchema } from "sr/utils/schemas/company/projects/BidSchema";
import { getPricingType } from "sr/utils/api/masterData/getPricingType";
import { getPaymentTerms } from "sr/utils/api/masterData/getPaymentTerms";

export const BidBasicDetails = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { requirement_id } = useParams();

  const [requirement, setRequirement] = useState<any>(null);
  const [loader, setLoader] = useState("");
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const validationErrorHandle = useValidationErrorHandle();
  const [pricingOptions, setPricingOptions] = useState<{ value: string; label: string }[]>([]);
  const [paymentOptions, setPaymentOptions] = useState<{ value: string; label: string }[]>([]);

  const userDetails = getUserDetails();

  useEffect(() => {
    if (requirement_id) {
      getRequirementDetails(requirement_id).then((res) => {
        if (res?.success) setRequirement(res.data);
      });
    }
  }, [requirement_id]);


  useEffect(() => {
    const load = async () => {
      try {
        const res = await getPricingType();
        if (res?.success && Array.isArray(res.data)) {
          setPricingOptions(
            res.data.map((item: any) => ({
              value: item.value,
              label: item.label,
            }))
          );
        }
      } catch {}
      try {
        const res = await getPaymentTerms();
        if (res?.success && Array.isArray(res.data)) {
          setPaymentOptions(
            res.data.map((item: any) => ({
              value: item.value,
              label: item.label,
            }))
          );
        }
      } catch {}
    };
    load();
  }, []);

  const formik = useFormik({
    initialValues: {
      company_id: userDetails?.user_id || "",
      corporate_id: requirement?.corporate_id?._id || "",
      requirement_id: requirement_id || "",
      description: "",
      experience: "",
      pricing_type: "",
      payment_terms: "",
      bid_amount: "",
      documents: [],
      status: "pending",
    },
    enableReinitialize: true,
    validationSchema: BidSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values) => {
      setLoader("submit");
      try {
        const uploadedDocs: any[] = [];
        for (const doc of documents) {
          if (doc.file) {
            try {
              const formData = new FormData();
              formData.append("task_img", doc.file);

              const res = await uploadFile(formData);

              if (res?.success && res.data?.files?.[0]?.location) {
                uploadedDocs.push({
                  doc_name: doc.name,
                  name: doc.name,
                  url: res.data.files[0].location,
                });
              } else {
                toast.error(`Failed to upload ${doc.name}`);
              }
            } catch (err) {
              console.error("Upload error:", err);
              toast.error(`Error uploading ${doc.name}`);
            }
          } else {
            uploadedDocs.push(doc);
          }
        }

        const payload = {
          ...values,
          bid_amount: String(values.bid_amount || '').replace(/[^0-9.]/g, ''),
          documents: uploadedDocs,
        };

        const result = await createBidding(payload);
        if (result?.success) {
          const newBidId = result.data?.id;
          navigate(`/projects/bid-review/${newBidId}`);
        }
      } catch (err) {
        toast.error(intl.formatMessage({ id: 'API.ERROR.BID.SAVE', defaultMessage: 'Error saving bidding' }));
      } finally {
        setLoader("");
      }
    },
  });

  useEffect(() => {
    scrollToError(formik.errors);
  }, [formik.errors]);

  const handleAttachmentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const pdfFiles = files.filter((file) => file.type === "application/pdf");
    if (pdfFiles.length === 0) {
      toast.error("Only PDF files are allowed");
      return;
    }

    const newDocs = pdfFiles.map((file) => ({
      doc_name: file.name,
      name: file.name,
      file,
    }));

    setDocuments((prev) => [...prev, ...newDocs]);
    e.target.value = "";
  };

  const handleAttachmentRemove = (index: number) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <MainWrapper>
      {loader === "submit" && <LoaderFullScreen />}
      <ContentWithSpinner loader={false} height="h-screen">
        <div className="container mx-auto max-w-4xl py-8 px-4">
          <RequirementCommonHeader
            title={
              <FormattedMessage
                id="CORPORATE.REQUIREMENT.POST.TITLE"
                defaultMessage="Bid Details"
              />
            }
          />

          <div className="bg-white rounded-2xl shadow p-8">
            <LevelBar progress={1} total={2} />
            <form onSubmit={formik.handleSubmit} className="space-y-10 mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  value={formik.values.experience}
                  onChange={(e) =>
                    formik.setFieldValue("experience", e.target.value)
                  }
                  error={formik.errors.experience}
                  label={
                    <FormattedMessage
                      id="RECRUITER.PROJECTS.INPUT.EXPERIENCE"
                      defaultMessage="Relevant Experience"
                    />
                  }
                  placeholder={intl.formatMessage({ id:'BID.PLACEHOLDER.EXPERIENCE', defaultMessage:'e.g. 5+ years in similar projects' })}
                  topinputdiv="!h-12"
                />

                <DropDown
                  label={
                    <FormattedMessage
                      id="RECRUITER.PROJECTS.INPUT.PRICING"
                      defaultMessage="Pricing Type"
                    />
                  }
                  value={formik.values.pricing_type}
                  onChange={(e) =>
                    formik.setFieldValue("pricing_type", e.target.value)
                  }
                  error={formik.errors.pricing_type as any}
                  className="h-11 mt-2 border border-zinc-800 rounded-lg"
                >
                  {(pricingOptions.length
                    ? pricingOptions
                    : [
                        { value: "fixed_price", label: "CORPORATE.RECRUITMENT.JOB.PRICINGTYPE.FIXED_PRICE" },
                        { value: "hourly_rate", label: "CORPORATE.RECRUITMENT.JOB.PRICINGTYPE.HOURLY_RATE" },
                      ]
                  ).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      <FormattedMessage id={opt.label} defaultMessage={opt.label} />
                    </option>
                  ))}
                </DropDown>

                <Input
                  value={formik.values.bid_amount}
                  onChange={(e) => {
                    const raw = String(e.target.value || '');
                    const sanitized = raw.replace(/[^0-9.]/g, '');
                    const parts = sanitized.split('.');
                    const intPart = parts[0] || '';
                    const decPart = parts[1] !== undefined ? parts[1].slice(0,2) : undefined;
                    const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    const formatted = decPart !== undefined ? `${withCommas}.${decPart}` : withCommas;
                    formik.setFieldValue('bid_amount', formatted)
                  }}
                  error={formik.errors.bid_amount}
                  label={
                    <FormattedMessage
                      id="RECRUITER.PROJECTS.INPUT.AMOUNT"
                      defaultMessage="Bid Amount"
                    />
                  }
                  placeholder={intl.formatMessage({ id:'BID.PLACEHOLDER.AMOUNT', defaultMessage:'Enter amount' })}
                  topinputdiv="!h-12"
                />

                <DropDown
                  label={
                    <FormattedMessage
                      id="RECRUITER.PROJECTS.INPUT.TERMS"
                      defaultMessage="Payment Terms"
                    />
                  }
                  value={formik.values.payment_terms}
                  onChange={(e) =>
                    formik.setFieldValue("payment_terms", e.target.value)
                  }
                  error={formik.errors.payment_terms as any}
                  className="h-11 mt-2 border border-zinc-800 rounded-lg"
                >
                  <option value="">Select Terms</option>
                  {(paymentOptions.length
                    ? paymentOptions
                    : [
                        { value: "60_40", label: "CORPORATE.RECRUITMENT.JOB.PAYMENTTERMS.SIXTY_ADVANCE_FORTY_COMPLETION" },
                        { value: "30_70", label: "CORPORATE.RECRUITMENT.JOB.PAYMENTTERMS.THIRTY_ADVANCE_SEVENTY_COMPLETION" },
                        { value: "milestone_based", label: "CORPORATE.RECRUITMENT.JOB.PAYMENTTERMS.MILESTONE_BASED" },
                        { value: "after_completion", label: "CORPORATE.RECRUITMENT.JOB.PAYMENTTERMS.AFTER_COMPLETION" },
                      ]
                  ).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      <FormattedMessage id={opt.label} defaultMessage={opt.label} />
                    </option>
                  ))}
                </DropDown>
              </div>

              {/* Description */}
              <div>
                <TextArea
                  rows={5}
                  label={
                    <FormattedMessage
                      id="RECRUITER.PROJECTS.INPUT.DESCRIPTION"
                      defaultMessage="Project Approach & Description"
                    />
                  }
                  placeholder={intl.formatMessage({ id:'BID.PLACEHOLDER.DESCRIPTION', defaultMessage:'Explain your approach, methodology, timelines, etc.' })}
                  value={formik.values.description}
                  onChange={(e) =>
                    formik.setFieldValue("description", e.target.value)
                  }
                  error={formik.errors.description}
                />
              </div>

              <DocumentUpload
                documents={documents}
                uploading={uploading}
                onUpload={handleAttachmentUpload}
                onRemove={handleAttachmentRemove}
              />

              <div className="px-6 py-4 flex justify-end">
                <Button
                  text={
                    <FormattedMessage
                      id="RECRUITER.PROJECTS.CONTINUE.BUTTON"
                      defaultMessage={"Submit Bid"}
                    />
                  }
                />
              </div>
            </form>
          </div>
        </div>
      </ContentWithSpinner>
    </MainWrapper>
  );
};

export default BidBasicDetails;
